import { yupResolver } from '@hookform/resolvers/yup'
import { findIndex, isEqual, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { ModalTitle } from '@/components'
import { AlertRatio } from '@/components/AlertRatio'
import { Button, DateTimePicker, Input, Modal } from '@/components/ui'
import { getTranslation } from '../../account/services/auth'
import {
  Artist,
  ArtistDetails,
  ArtistPayload,
  Languages,
} from '../../common/models'
import FormGroup from '../../core/components/FormGroup'
import { FormUploadPreview } from '../../core/components/FormUploadPreview'
import { formatDateTime } from '../../core/constants/times'
import {
  createArtist,
  deleteArtist,
  getAllArtists,
  updateArtist,
} from '../../core/services/artist'
import translate from '../../core/services/translate'
import { uploadImage } from '../../core/services/upload-image'
import { ArtistCheckboxField } from './artist/ArtistCheckboxField'
import { ArtistLanguageCard } from './artist/ArtistLanguageCard'
import { ArtistSectionTitle } from './artist/ArtistSectionTitle'
import { ArtistSocialFields } from './artist/ArtistSocialFields'
import { IArtistContent, IArtistValues } from './artist/types'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { MessageBox } from './messages/MessageBox'
import { Icon } from '@/common/components/Icon'

export interface CreateArtistModalProps {
  showEditModal: boolean
  artist?: ArtistDetails
  setShowEditModal: (show: boolean) => void
  setArtists: (data: Artist[]) => void
  setTotalElemenets: (data: number) => void
  setPage: (data: number) => void
  editMode: boolean
  size: number
  page: number
  allLanguages: Languages[]
}

export const CreateArtistModal = (
  props: CreateArtistModalProps,
): JSX.Element => {
  const {
    showEditModal,
    setShowEditModal,
    artist,
    editMode,
    setArtists,
    page,
    setPage,
    size,
    setTotalElemenets,
    allLanguages,
  } = props
  const [image, setImage] = useState('')
  const [imageName, setImageName] = useState('')
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [invalidPhoto, setInvalidPhoto] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [content, setContent] = useState<IArtistContent[]>([])

  const language = getTranslation()

  useEffect(() => {
    const temp = map(allLanguages, (lang) => {
      return {
        locale: lang.locale,
        description: '',
      }
    })
    setValue('content', temp)
    if (artist) {
      if (!!artist.translations && artist.translations.length > 0) {
        setContent(artist.translations)
        setTimeout(() => {
          map(artist.translations, (trans) => {
            const index = findIndex(
              allLanguages,
              (l) => l.locale === trans.locale,
            )
            if (index !== -1) {
              setValue(`content.${index}.description`, trans.description)
              setValue(`content.${index}.locale`, trans.locale)
            }
          })
        }, 100)
      }
      setValue('name', artist.name, { shouldValidate: true })
      setValue('publishedDate', formatDateTime(artist.publishedAt.valueOf()))
      setValue('published', artist.published, { shouldValidate: true })
      setValue('weight', artist.weight, { shouldValidate: true })
      setValue('webLink', artist.webLink)
      setValue('fbLink', artist.fbLink)
      setValue('twLink', artist.twLink)
      setValue('youtubeLink', artist.youtubeLink)
      setValue('igLink', artist.igLink)
      setValue('tiktokLink', artist.tiktokLink)
      setValue('defaultImage', artist.defaultImage)
      setValue('photo', artist.imageLink)
      setImage(artist.imageLink)
      if (artist?.imageLink) {
        const url = new URL(artist?.imageLink)
        const i = url?.pathname?.lastIndexOf('/') + 1
        const imageName = url?.pathname?.substring(i)
        setImageName(imageName)
      }
    }
    return () => {
      setImage('')
      setImageName('')
      setInvalidPhoto(false)
      setIsErrorRatio(false)
      reset()
    }
  }, [artist, showEditModal])

  const handleClose = () => {
    setShowEditModal(false)
    setImage('')
    setImageName('')
  }

  const schema = yup.object().shape({
    content: yup
      .array()
      .of(
        yup.object().shape({
          locale: yup.string().nullable().required('Field is required'),
          description: yup.string().required('Field is required'),
        }),
      )
      .required(),
    name: yup.string().required('Field is required'),
    weight: yup.number().required('Field is required'),
    published: yup.boolean().required('Field is required'),
    defaultImage: yup.boolean(),
    photo: yup.string().when('defaultImage', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) => schema.required(),
    }),
    webLink: yup.string(),
    twLink: yup.string(),
    fbLink: yup.string(),
    youtubeLink: yup.string(),
    tiktokLink: yup.string(),
    igLink: yup.string(),
    publishedDate: yup.string(),
  })

  const {
    handleSubmit,
    control,
    register,
    formState: { errors, dirtyFields, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<IArtistValues>({
    mode: 'onChange',
    resolver: yupResolver<IArtistValues>(schema),
    defaultValues: {
      name: '',
      weight: undefined,
      publishedDate: formatDateTime(new Date().valueOf()),
      published: false,
      webLink: '',
      fbLink: '',
      twLink: '',
      igLink: '',
      youtubeLink: '',
      tiktokLink: '',
      photo: '',
      content: [],
    },
  })

  const contentWatch = watch('content')
  const dafaultImageWatch = watch('defaultImage')

  const submit = async (values: IArtistValues) => {
    const data: ArtistPayload = {
      name: values.name,
      translations: values.content,
      weight: values.weight,
      publishedAt: values.publishedDate
        ? new Date(values.publishedDate).valueOf()
        : new Date().valueOf(),
      published: values.published,
      webLink: values.webLink,
      fbLink: values.fbLink,
      twLink: values.twLink,
      igLink: values.igLink,
      youtubeLink: values.youtubeLink,
      tiktokLink: values.tiktokLink,
      defaultImage: values.defaultImage,
    }
    if (values.photo) {
      data.imageLink = values.photo
      data.thumbnailLink = values.photo
    }
    if (artist?.id) {
      try {
        const { status } = await updateArtist(artist.id, data)
        const response = await getAllArtists({ page: 0, size })
        setPage(1)
        setArtists(response.data.content)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    } else {
      try {
        const { status } = await createArtist(data)
        const response = await getAllArtists({ page, size })
        setArtists(response.data.content)
        setTotalElemenets(response.data.totalElements)
        if (status === 200 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Create Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    }
    handleClose()
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const deleteArtistFromModal = () => {
    // prettier-ignore
    (async () => {
      try {
        if (artist) {
          const { status } = await deleteArtist([artist.id])

          const response = await getAllArtists({ page, size })
          setArtists(response.data.content)
          setTotalElemenets(response.data.totalElements)
          if (response.data.totalPages <= page) {
            setPage(response.data.pageNumber)
          } else {
            setPage(response.data.pageNumber + 1)
          }
          if (status === 204 && response.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
    handleClose()
  }

  return (
    <>
      <Modal
        open={showEditModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        contentClassName="md:max-w-[940px]"
        wrapperClassName="max-h-[80svh] overflow-y-auto px-8 py-[30px]"
        headerClassName="mb-[28px] space-y-0"
        footerClassName="gap-[15px] sm:space-x-0"
        title={
          <div className="border-b border-monochrome-120 pb-[28px]">
            <ModalTitle
              icon="profile-delete"
              title={artist ? artist.name : 'Add New Artist'}
            />
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between gap-[15px]">
            {editMode ? (
              <Button
                onClick={handleDelete}
                variant="destructive"
                className="mr-auto w-[140px] !min-w-[140px] border border-monochrome-120"
              >
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-[15px]">
              <Button onClick={handleClose} type="button" variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={
                  isSubmitting ||
                  isUploading ||
                  (Object.keys(dirtyFields).length === 0 &&
                    isEqual(contentWatch, content)) ||
                  (isErrorRatio && !dafaultImageWatch)
                }
                form="artist-form"
                type="submit"
                variant="primary"
                loading={isSubmitting || isUploading}
              >
                {artist?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          </div>
        }
      >
        <form
          id="artist-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-[25px]"
        >
          <ArtistSectionTitle>Main Info</ArtistSectionTitle>

          <FormGroup label="Name" error={errors?.name?.message}>
            <Input
              isInvalid={!!errors?.name?.message}
              {...register('name')}
              type="text"
              placeholder="Enter Name"
            />
          </FormGroup>

          {map(allLanguages, (lang, index) => (
            <ArtistLanguageCard
              key={lang.id}
              languageName={lang.name}
              index={index}
              control={control}
              setValue={setValue}
              value={contentWatch?.[index]?.description || ''}
              isError={!!errors?.content?.[index]?.description?.message}
            />
          ))}

          <FormGroup
            label="Weight"
            tooltip={translate(language.value, 'artists.weightTooltip')}
          >
            <Input
              isInvalid={Boolean(errors?.weight?.message)}
              {...register('weight')}
              type="text"
              placeholder="Enter Weight"
            />
          </FormGroup>

          <FormGroup label="Date">
            <DateTimePicker
              hasError={!!errors?.publishedDate?.message}
              {...register('publishedDate')}
              onChange={(date) => {
                register('publishedDate').onChange({
                  target: {
                    value: date ? formatDateTime(date.valueOf()) : '',
                  },
                })
                setValue(
                  'publishedDate',
                  date ? formatDateTime(date.valueOf()) : '',
                  { shouldValidate: true },
                )
              }}
              placeholder="Enter Publish Date"
            />
          </FormGroup>

          <ArtistCheckboxField
            name="published"
            label="Published"
            control={control}
          />

          <ArtistSectionTitle>Social Media</ArtistSectionTitle>

          <ArtistSocialFields register={register} errors={errors} />

          <div className="h-px w-full bg-monochrome-120" />

          <ArtistSectionTitle>Photo</ArtistSectionTitle>

          <ArtistCheckboxField
            name="defaultImage"
            label="Default Image"
            control={control}
          />

          <FormUploadPreview
            disabled={dafaultImageWatch}
            control={
              <Input
                multiple={false}
                accept="image/*"
                isInvalid={Boolean(
                  errors?.photo?.message || invalidPhoto || isErrorRatio,
                )}
                disabled={dafaultImageWatch}
                name="photo"
                onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                  const file = e?.target?.files?.[0]
                  if (file) {
                    setInvalidPhoto(false)
                    setIsErrorRatio(false)
                    if (file.size > 1_000_000) {
                      setInvalidPhoto(true)
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage('The photo is too large (1 MB)')
                      return
                    }
                    const data = await uploadImage(
                      file,
                      setIsUploading,
                      setIsError,
                      setShowMessageBox,
                      setMessage,
                      setIsErrorRatio,
                      'ARTIST',
                    )
                    if (data.photoUrl) {
                      setImage(data.photoUrl)
                      setImageName(file.name)
                      setValue('photo', data.photoUrl, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  }
                }}
                type="file"
                className="bg-monochrome-5-50"
              />
            }
            imageName={imageName}
            isUploading={isUploading}
            url={image}
            onRemove={() => {
              setImage('')
              setImageName('')
              setValue('photo', '')
            }}
            imageStyle={{
              objectFit: 'contain',
            }}
            label="Photo (335x335)"
            previewLabel="Photo (335x335)"
          />
          {isErrorRatio && <AlertRatio />}
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle={'Artist'}
        deleteFunc={deleteArtistFromModal}
        deletedItem={artist?.name}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
